import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Copy,
  ExternalLink,
  Eye,
  Link2,
  type LucideIcon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UnitListRow } from '$lib/api/types';
import { copyToClipboard } from '$lib/lib/clipboard';
import { cn } from '$lib/lib/cn';

// Decision 7: right-click context menu, single kebab fallback. Both
// affordances open the SAME menu — this component renders it once via a
// portal so the trigger source doesn't matter.

export interface RowMenuAnchor {
  /** Pointer coordinate when triggered by right-click. */
  x: number;
  y: number;
  /** Source element bounds when triggered by kebab — menu anchors relative to this. */
  rect?: DOMRect;
  /** The row the menu is acting on. */
  row: UnitListRow;
}

export interface RowMenuProps {
  anchor: RowMenuAnchor | null;
  onClose: () => void;
  onOpen: (row: UnitListRow) => void;
  onOpenInNewTab: (row: UnitListRow) => void;
}

interface MenuAction {
  id: string;
  label: string;
  icon: LucideIcon;
  shortcut?: string;
  disabled?: boolean;
  hint?: string;
  run: () => void | Promise<void>;
}

export function RowMenu({ anchor, onClose, onOpen, onOpenInNewTab }: RowMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [feedback, setFeedback] = useState<string | null>(null);
  const id = useId();
  const { t } = useTranslation();

  const actions: MenuAction[] = useMemo(() => {
    if (!anchor) return [];
    const row = anchor.row;
    return [
      {
        id: 'open',
        label: t('rowMenu.openUnit'),
        icon: Eye,
        shortcut: 'Enter',
        run: () => onOpen(row)
      },
      {
        id: 'open-new-tab',
        label: t('rowMenu.openInNewTab'),
        icon: ExternalLink,
        shortcut: '⌘ ↵',
        run: () => onOpenInNewTab(row)
      },
      {
        id: 'copy-address',
        label: t('rowMenu.copyAddress'),
        icon: Copy,
        run: async () => {
          const ok = await copyToClipboard(row.address);
          setFeedback(ok ? t('rowMenu.addressCopied') : t('rowMenu.copyFailed'));
        }
      },
      {
        id: 'copy-link',
        label: t('rowMenu.copyShareLink'),
        icon: Link2,
        hint: t('rowMenu.includesFilters'),
        run: async () => {
          const url = `${window.location.origin}/units/${row.id}${window.location.search}`;
          const ok = await copyToClipboard(url);
          setFeedback(ok ? t('rowMenu.linkCopied') : t('rowMenu.copyFailed'));
        }
      }
    ];
  }, [anchor, onOpen, onOpenInNewTab, t]);

  // Position the menu either at the pointer (right-click) or anchored to the
  // kebab button. Adjust before paint so the user never sees the menu jump.
  useLayoutEffect(() => {
    if (!anchor || !ref.current) return;
    const menu = ref.current;
    const rect = menu.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;
    let left: number;
    let top: number;
    if (anchor.rect) {
      // Below-right of the kebab; flip to above if no room.
      left = anchor.rect.right - rect.width;
      top = anchor.rect.bottom + 4;
      if (top + rect.height + margin > vh) top = anchor.rect.top - rect.height - 4;
    } else {
      left = anchor.x;
      top = anchor.y;
    }
    if (left + rect.width + margin > vw) left = vw - rect.width - margin;
    if (left < margin) left = margin;
    if (top + rect.height + margin > vh) top = Math.max(margin, vh - rect.height - margin);
    if (top < margin) top = margin;
    setPosition({ left, top });
  }, [anchor]);

  useEffect(() => {
    if (!anchor) return;
    setActiveIndex(0);
    setFeedback(null);
  }, [anchor]);

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => {
      setFeedback(null);
      onClose();
    }, 800);
    return () => window.clearTimeout(timer);
  }, [feedback, onClose]);

  useEffect(() => {
    if (!anchor) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => {
          const enabled = actions
            .map((a, idx) => ({ a, idx }))
            .filter(({ a }) => !a.disabled);
          if (enabled.length === 0) return i;
          const cur = enabled.findIndex(({ idx }) => idx === i);
          return enabled[(cur + 1) % enabled.length].idx;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => {
          const enabled = actions
            .map((a, idx) => ({ a, idx }))
            .filter(({ a }) => !a.disabled);
          if (enabled.length === 0) return i;
          const cur = enabled.findIndex(({ idx }) => idx === i);
          return enabled[(cur - 1 + enabled.length) % enabled.length].idx;
        });
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const action = actions[activeIndex];
        if (action && !action.disabled) void action.run();
      }
    }
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [anchor, actions, activeIndex, onClose]);

  if (!anchor) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      id={id}
      aria-label={t('rowMenu.actionsFor', { address: anchor.row.address })}
      data-testid="row-menu"
      style={{
        position: 'fixed',
        left: position?.left ?? -9999,
        top: position?.top ?? -9999,
        visibility: position ? 'visible' : 'hidden'
      }}
      className="z-50 min-w-[260px] rounded-sm border border-rule bg-surface py-1 text-sm shadow-elev-3"
    >
      <div className="border-b border-rule px-3 py-1.5 text-2xs text-fg-subtle">
        {anchor.row.address}
      </div>
      {actions.map((action, idx) => {
        const Icon = action.icon;
        const active = idx === activeIndex && !action.disabled;
        return (
          <button
            type="button"
            key={action.id}
            role="menuitem"
            data-testid={`row-menu-${action.id}`}
            disabled={action.disabled}
            onMouseEnter={() => !action.disabled && setActiveIndex(idx)}
            onClick={() => {
              if (action.disabled) return;
              void action.run();
              if (action.id !== 'copy-address' && action.id !== 'copy-link') onClose();
            }}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors duration-fast',
              action.disabled && 'cursor-not-allowed text-fg-subtle',
              !action.disabled && active && 'bg-accent/10 text-accent',
              !action.disabled && !active && 'text-fg hover:bg-surface-2'
            )}
          >
            <Icon size={14} className="shrink-0 opacity-80" />
            <span className="flex-1">{action.label}</span>
            {action.shortcut && (
              <span className="num text-2xs text-fg-subtle">{action.shortcut}</span>
            )}
            {action.hint && !action.shortcut && (
              <span className="text-2xs text-fg-subtle">{action.hint}</span>
            )}
          </button>
        );
      })}
      {feedback && (
        <div
          role="status"
          className="border-t border-rule bg-surface-2 px-3 py-1 text-2xs text-positive"
        >
          {feedback}
        </div>
      )}
    </div>,
    document.body
  );
}
